import { Order } from '../../model/dto/order/order';
import { OrderDeliveryState } from '../../model/type/order-delivery-state.type';
import { OrderDropshippingPartner } from '../../model/type/order-dropshipping-partner.type';
import { StatusTone } from '../../model/type/status-tone.type';

export const ORDER_DELIVERY_OVERDUE_DAYS = 30;

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const CARRIER_DELIVERED_STATUS = 'delivered';

const DELIVERY_STATUS_LABEL_KEYS = new Map<string, string>([
  ['pre_transit', 'orders.delivery.statuses.preTransit'],
  ['in_transit', 'orders.delivery.statuses.inTransit'],
  ['out_for_delivery', 'orders.delivery.statuses.outForDelivery'],
  ['delivered', 'orders.delivery.statuses.delivered'],
  ['returned_to_sender', 'orders.delivery.statuses.returnedToSender'],
]);

const DELIVERY_STATUS_TONES = new Map<string, StatusTone>([
  ['pre_transit', 'pending'],
  ['in_transit', 'progress'],
  ['out_for_delivery', 'progress'],
  ['returned_to_sender', 'caution'],
]);

type DeliveryFields = Pick<
  Order,
  | 'shipped_at'
  | 'estimated_delivery_at'
  | 'delivered_at'
  | 'delivery_status'
  | 'dropshipping_partner'
>;

const parseTimestamp = (value?: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const timestamp = new Date(value).getTime();

  return isNaN(timestamp) ? undefined : timestamp;
};

export const getDaysSinceShipped = (
  order: Pick<Order, 'shipped_at'>,
  now: Date = new Date()
): number | undefined => {
  const shippedAt = parseTimestamp(order.shipped_at);

  if (shippedAt === undefined || shippedAt > now.getTime()) {
    return undefined;
  }

  return Math.floor((now.getTime() - shippedAt) / DAY_IN_MILLISECONDS);
};

export const isCarrierDeliveredWithoutDate = (
  order: Pick<Order, 'delivered_at' | 'delivery_status'>
): boolean =>
  parseTimestamp(order.delivered_at) === undefined &&
  order.delivery_status?.trim().toLowerCase() === CARRIER_DELIVERED_STATUS;

export const getOrderDeliveryState = (
  order: DeliveryFields,
  now: Date = new Date()
): OrderDeliveryState => {
  if (order.delivered_at && parseTimestamp(order.delivered_at) !== undefined) {
    return { kind: 'delivered', deliveredAt: order.delivered_at };
  }

  const daysSinceShipped = getDaysSinceShipped(order, now);

  if (
    daysSinceShipped !== undefined &&
    daysSinceShipped >= ORDER_DELIVERY_OVERDUE_DAYS
  ) {
    return { kind: 'overdue', daysSinceShipped };
  }

  if (isCarrierDeliveredWithoutDate(order)) {
    return { kind: 'notConfirmed' };
  }

  if (
    order.shipped_at ||
    order.estimated_delivery_at ||
    order.delivery_status
  ) {
    return {
      kind: 'inTransit',
      deliveryStatus: order.delivery_status ?? undefined,
    };
  }

  return order.dropshipping_partner === OrderDropshippingPartner.OpsEngine
    ? { kind: 'noData' }
    : { kind: 'unavailable' };
};

export const getDeliveryStatusLabel = (
  deliveryStatus: string,
  translate: (key: string) => string
): string => {
  const labelKey = DELIVERY_STATUS_LABEL_KEYS.get(deliveryStatus);

  return labelKey ? translate(labelKey) : deliveryStatus.replace(/_/g, ' ');
};

export const getDeliveryStatusTone = (deliveryStatus?: string): StatusTone => {
  if (!deliveryStatus) {
    return 'progress';
  }

  return DELIVERY_STATUS_TONES.get(deliveryStatus) ?? 'neutral';
};
